import React from "react";
import { motion } from "framer-motion";
import {
  Handshake,
  Building2,
  Stethoscope,
  HeartPulse,
  Users,
  Network,
  ShieldCheck,
  TrendingUp,
  Lightbulb,
  ArrowRight,
  Check,
  Globe2,
  MessageSquare,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const Partners = () => {
  const partnershipTypes = [
    {
      icon: Stethoscope,
      title: "Healthcare Partners",
      description:
        "Connect with Doxzon to support healthcare delivery and create more connected experiences for patients.",
    },
    {
      icon: Building2,
      title: "Healthcare Organisations",
      description:
        "Explore opportunities to connect your organisation with digital healthcare workflows.",
    },
    {
      icon: Network,
      title: "Technology Partners",
      description:
        "Work with Doxzon to connect complementary technology and build better healthcare solutions.",
    },
    {
      icon: Users,
      title: "Strategic Partners",
      description:
        "Collaborate on initiatives that can expand access, improve experiences and create meaningful healthcare outcomes.",
    },
  ];

  const benefits = [
    {
      icon: Network,
      title: "Connected Ecosystem",
      description:
        "Become part of an ecosystem designed to connect patients, professionals and healthcare services.",
    },
    {
      icon: TrendingUp,
      title: "Grow Together",
      description:
        "Explore opportunities to create sustainable value through collaboration.",
    },
    {
      icon: Lightbulb,
      title: "Meaningful Innovation",
      description:
        "Work together on ideas that address real challenges in healthcare.",
    },
    {
      icon: ShieldCheck,
      title: "Built on Trust",
      description:
        "Create partnerships with a focus on responsible technology and healthcare experiences.",
    },
  ];

  const steps = [
    {
      number: "01",
      title: "Start a Conversation",
      description:
        "Tell us about your organisation, your goals and how you think we could work together.",
    },
    {
      number: "02",
      title: "Explore Opportunities",
      description:
        "We'll learn more about your needs and identify potential areas of collaboration.",
    },
    {
      number: "03",
      title: "Define the Partnership",
      description:
        "Work together to establish a partnership model that makes sense for both organisations.",
    },
    {
      number: "04",
      title: "Build Together",
      description:
        "Turn the partnership into meaningful products, services or healthcare experiences.",
    },
  ];

  const principles = [
    "Shared goals",
    "Patient-focused thinking",
    "Responsible innovation",
    "Open collaboration",
    "Long-term relationships",
    "Meaningful healthcare impact",
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO
        ====================================================== */}
        <section className="relative overflow-hidden bg-[#f4fbfc]">

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />

          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-16 items-center">

            {/* HERO CONTENT */}
            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Doxzon Partners
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Let's build
                <span className="block text-teal-500 mt-2">
                  better healthcare together.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Doxzon works with healthcare organisations, professionals and
                technology partners to create more connected healthcare
                experiences.
              </p>

              <div className="flex flex-wrap gap-3 mt-9">

                <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-slate-800 transition">

                  Become a Partner

                  <ArrowRight size={16} />

                </button>

                <button className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 transition">

                  Explore Partnerships

                </button>

              </div>

            </motion.div>

            {/* HERO VISUAL */}
            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative rounded-[32px] bg-white border border-slate-100 shadow-[0_30px_80px_rgba(15,23,42,0.14)] p-7">

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Partnership Ecosystem
                    </p>

                    <h3 className="font-sora text-xl font-semibold mt-1">
                      Connected by Doxzon
                    </h3>

                  </div>

                  <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                    <Handshake
                      size={24}
                      className="text-teal-600"
                    />

                  </div>

                </div>

                {/* CENTRAL CONNECTION */}
                <div className="relative flex justify-center my-10">

                  <div className="absolute w-56 h-56 rounded-full border border-teal-100" />

                  <div className="absolute w-40 h-40 rounded-full border border-teal-100" />

                  <div className="relative z-10 w-24 h-24 rounded-[28px] bg-slate-950 grid place-items-center shadow-xl">

                    <Handshake
                      size={38}
                      className="text-teal-400"
                    />

                  </div>

                </div>

                {/* PARTNER TYPES */}
                <div className="grid grid-cols-2 gap-3">

                  {[
                    [Stethoscope, "Healthcare"],
                    [Building2, "Organisations"],
                    [Network, "Technology"],
                    [Users, "Strategic"],
                  ].map(([Icon, title]) => (

                    <div
                      key={title}
                      className="rounded-2xl bg-slate-50 border border-slate-100 p-4"
                    >

                      <div className="w-9 h-9 rounded-xl bg-white grid place-items-center">

                        <Icon
                          size={17}
                          className="text-teal-600"
                        />

                      </div>

                      <p className="text-xs font-semibold mt-3">
                        {title}
                      </p>

                    </div>

                  ))}

                </div>

              </div>

            </motion.div>

          </div>

        </section>

        {/* =====================================================
            WHY PARTNER
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Why Partner With Doxzon
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Better together,
                <span className="block text-teal-500">
                  from the start.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Partnerships can bring together different capabilities,
                perspectives and expertise to create stronger healthcare
                experiences.
              </p>

            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">

              {benefits.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -6 }}
                    className="rounded-3xl border border-slate-100 bg-slate-50/70 p-7 hover:bg-white hover:shadow-xl hover:border-teal-100 transition"
                  >

                    <div className="w-12 h-12 rounded-2xl bg-teal-50 grid place-items-center">

                      <Icon
                        size={22}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-lg font-semibold mt-6">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-7 mt-3">
                      {item.description}
                    </p>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            PARTNERSHIP TYPES
        ====================================================== */}
        <section className="py-24 md:py-28 bg-[#f6fbfc]">

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Partnership Opportunities
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Find the right way
                <span className="text-teal-500">
                  {" "}to work together.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Whether you provide healthcare services, technology or
                strategic expertise, there may be an opportunity to collaborate.
              </p>

            </div>

            <div className="grid md:grid-cols-2 gap-5 mt-12">

              {partnershipTypes.map((item, index) => {

                const Icon = item.icon;

                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.07 }}
                    whileHover={{ y: -5 }}
                    className="group rounded-3xl bg-white border border-slate-100 p-7 md:p-8 hover:border-teal-100 hover:shadow-xl transition"
                  >

                    <div className="flex gap-5">

                      <div className="w-14 h-14 rounded-2xl bg-teal-50 grid place-items-center shrink-0 group-hover:bg-teal-500 transition">

                        <Icon
                          size={24}
                          className="text-teal-600 group-hover:text-white transition"
                        />

                      </div>

                      <div>

                        <h3 className="font-sora text-xl font-semibold">
                          {item.title}
                        </h3>

                        <p className="text-slate-500 text-sm leading-7 mt-3">
                          {item.description}
                        </p>

                        <button className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-5">

                          Explore Partnership

                          <ArrowRight size={15} />

                        </button>

                      </div>

                    </div>

                  </motion.div>
                );

              })}

            </div>

          </div>

        </section>

        {/* =====================================================
            PARTNERSHIP JOURNEY
        ====================================================== */}
        <section className="py-24 md:py-28 bg-slate-950 text-white">

          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                How It Works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                From conversation to
                <span className="block text-teal-400">
                  collaboration.
                </span>

              </h2>

            </div>

            <div className="relative grid md:grid-cols-4 gap-6 mt-14">

              <div className="hidden md:block absolute top-7 left-[11%] right-[11%] h-px bg-white/10" />

              {steps.map((step) => (

                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="relative text-center"
                >

                  <div className="relative z-10 mx-auto w-14 h-14 rounded-2xl bg-teal-500 border-4 border-slate-950 shadow-lg grid place-items-center">

                    <span className="text-white text-xs font-bold">
                      {step.number}
                    </span>

                  </div>

                  <h3 className="font-sora text-lg font-semibold mt-6">
                    {step.title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-6 mt-2 max-w-[230px] mx-auto">
                    {step.description}
                  </p>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            PARTNERSHIP PRINCIPLES
        ====================================================== */}
        <section className="py-24 md:py-28">

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Our Approach
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Partnerships built
                <span className="block text-teal-500">
                  around shared value.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                The strongest partnerships are built on shared goals,
                complementary strengths and a clear focus on the people
                healthcare ultimately serves.
              </p>

              <div className="flex items-center gap-3 mt-7">

                <div className="w-11 h-11 rounded-xl bg-teal-50 grid place-items-center">

                  <HeartPulse
                    size={19}
                    className="text-teal-600"
                  />

                </div>

                <span className="text-sm font-semibold text-slate-700">
                  Healthcare remains at the centre.
                </span>

              </div>

            </div>

            <div className="space-y-3">

              {principles.map((item) => (

                <motion.div
                  key={item}
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4"
                >

                  <span className="w-8 h-8 rounded-full bg-white text-teal-600 grid place-items-center shrink-0">

                    <Check size={15} />

                  </span>

                  <span className="text-sm md:text-base font-medium text-slate-700">
                    {item}
                  </span>

                </motion.div>

              ))}

            </div>

          </div>

        </section>

        {/* =====================================================
            CTA
        ====================================================== */}
        <section className="px-6 pb-24">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[32px] bg-[#f4fbfc] border border-teal-50 px-8 py-16 md:px-16">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-200/30 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-200/30 blur-3xl" />

            <div className="relative grid lg:grid-cols-2 gap-10 items-center">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  Start a Partnership
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  Have an idea?
                  <span className="block text-teal-500">
                    Let's talk.
                  </span>

                </h2>

                <p className="text-slate-600 leading-7 mt-5 max-w-xl">
                  Tell us about your organisation, your goals and what you
                  would like to build together.
                </p>

              </div>

              <div className="flex lg:justify-end">

                <button className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-7 h-14 font-semibold hover:bg-teal-500 transition">

                  <MessageSquare size={18} />

                  Become a Doxzon Partner

                  <ArrowRight size={17} />

                </button>

              </div>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Partners;